/**
 * A modal that asks for reauthentication before a user undertakes sensitive
 * actions from the settings page.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import React, {memo, useEffect, useRef, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {useFlagOrCookie} from "@khan/analytics-growthbook";
import {AppleButton} from "@khan/auth-apple";
import {ClasslinkButton} from "@khan/auth-classlink";
import {
    CleverLibraryActiveWrapper,
    CleverLibraryButton,
} from "@khan/auth-clever";
import {FacebookButton} from "@khan/auth-facebook";
import {GoogleButton} from "@khan/auth-google";
import {MicrosoftButton} from "@khan/auth-microsoft";
import {
    getLoginErrorMessageByCode,
    PasswordTextField,
    reauthWithPassword,
} from "@khan/auth-password";
import {authType, TextFieldWithInfo} from "@khan/auth-shared";
import {useGql} from "@khan/data-gql";
import {doNotTranslateYet} from "@khan/i18n";
import {mediaQueries} from "@khan/shared-styles";
import {useLocation} from "@khan/urls";

const strings = {
    ssoFailureBody: (loginType: string) =>
        t`We couldn't continue with ${loginType} due to an error. Try again or log in with a different method.`,
} as const;

// Auth code is of form 123456
const TWO_FACTOR_AUTH_CODE_LENGTH = 6;
// Recovery code is of form abcde-12345
const TWO_FACTOR_RECOVERY_CODE_LENGTH = 11;

type LoginState = "credentials" | "authenticator" | "recovery";

interface Props {
    // Either an email or username
    identifier: string | null | undefined;
    onClose: () => unknown;
    onSuccess: () => unknown;
    onFailure: () => unknown;
    hasPasswordField: boolean;
    hasGoogleButton: boolean;
    hasFacebookButton: boolean;
    hasMicrosoftButton: boolean;
    hasAppleButton: boolean;
    inlineForTests?: boolean;
    showClasslinkAuthProvider: boolean;
    // Some authentication providers – such as Apple and Clever – may
    // navigate the user away from khanacademy.org to their own site
    // to complete authentication. After authentication is complete
    // the provider will redirect the user to continueUrl.
    //
    // If no value is provided the user will be returned to the location
    // they left Khan Academy from.
    continueUrl?: string;
}

const ReauthModal = ({
    identifier,
    onSuccess,
    onFailure,
    onClose,
    hasPasswordField,
    hasGoogleButton,
    hasFacebookButton,
    hasAppleButton,
    hasMicrosoftButton,
    inlineForTests,
    showClasslinkAuthProvider,
    continueUrl: propsContinueUrl,
}: Props) => {
    const location = useLocation();
    const gqlFetch = useGql();

    // Convert state to useState hooks
    const [password, setPassword] = useState<string | null | undefined>(null);
    const [isValid, setIsValid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);
    const [ssoErrorState, setSsoErrorState] = useState<string | null>(null);
    const [loginState, setLoginState] = useState<LoginState>("credentials");
    const [twoFactorAuthCode, setTwoFactorAuthCode] = useState("");
    const [twoFactorRecoveryCode, setTwoFactorRecoveryCode] = useState("");

    // Replicate the mounted check using a ref
    const isMounted = useRef(true);

    const shouldShowMicrosoft = useFlagOrCookie("microsoft-sso");

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const doReauthLogin = (
        passwordValue: string,
        twoFactorAuthCodeValue?: string,
        twoFactorRecoveryCodeValue?: string,
    ) => {
        setIsSubmitting(true);

        reauthWithPassword(
            gqlFetch,
            passwordValue,
            twoFactorAuthCodeValue,
            twoFactorRecoveryCodeValue,
        )
            .then((res) => {
                if (!isMounted.current) {
                    return;
                }
                setIsSubmitting(false);
                onSuccess();
            })
            .catch((error) => {
                if (!isMounted.current) {
                    return;
                }

                // This is a signal that two factor authentication is enabled,
                // and we need to show the 2FA form so users can input their
                // verification codes.
                if (error.message === "TWO_FACTOR_CODE_REQUIRED") {
                    setIsSubmitting(false);
                    setLoginState("authenticator");
                    setServerError(null);
                    return;
                }

                // This is a signal that the two factor code is incorrect,
                // and we should kick back to the reauth form, emptied out
                // for security purposes.
                if (
                    error.message === "TWO_FACTOR_CODE_INCORRECT" ||
                    error.message === "TWO_FACTOR_RECOVERY_CODE_INVALID"
                ) {
                    setIsSubmitting(false);
                    setLoginState("credentials");
                    setPassword(null);
                    setTwoFactorAuthCode("");
                    setTwoFactorRecoveryCode("");
                    setServerError(getLoginErrorMessageByCode(error.message));
                    return;
                }

                setIsSubmitting(false);
                setServerError(t`This password is incorrect.`);
            });
    };

    const handleSubmit = () => {
        if (identifier && password) {
            doReauthLogin(
                password,
                twoFactorAuthCode.replace(" ", ""),
                twoFactorRecoveryCode,
            );
        } else {
            onFailure();
        }
    };

    const renderLoginButtonText = (): string => {
        return isSubmitting ? t`Logging you in...` : t`Log in`;
    };

    /**
     * We add an error state in case the reauth flow fails.
     *
     * @param {string} loginType The third-party provider (capitalized)
     */
    const onSSOError = (loginType: string) => {
        setSsoErrorState(loginType);
    };

    const renderOAuthButtons = (): React.ReactNode => {
        const effectiveContinueUrl =
            propsContinueUrl ||
            location.pathname + location.search + location.hash;

        // Regardless of what options the props ask for, many of them require
        // the user to have an identifier (email or username) for the reauth to
        // work. We'll only show those options if `hasIdentifier` is true.
        const hasIdentifier = Boolean(identifier);

        return (
            <View
                style={[
                    styles.oAuthButtons,
                    !(hasPasswordField && hasIdentifier) &&
                        styles.oAuthButtonsNoPassword,
                ]}
            >
                {hasGoogleButton && hasIdentifier && (
                    <View style={styles.oAuthButtonWrapper}>
                        <GoogleButton
                            reauth={true}
                            onSuccess={onSuccess}
                            onReauthError={() => onSSOError("Google")}
                            size="large"
                            continueUrl={effectiveContinueUrl}
                            context={authType.REAUTH}
                        />
                    </View>
                )}
                {hasFacebookButton && (
                    <View style={styles.oAuthButtonWrapper}>
                        <FacebookButton
                            onReauthSuccess={(url) => onSuccess()}
                            onReauthError={(url) => onSSOError("Facebook")}
                            size="large"
                            continueUrl={effectiveContinueUrl}
                            context={authType.REAUTH}
                        />
                    </View>
                )}
                <CleverLibraryActiveWrapper
                    // We usually hide Clever Library in certain locales... but
                    // if the user has no identifier, they're probably a Clever
                    // user, and they probably _need_ this reauth method! So,
                    // if they have no identifier, we force Clever to show.
                    alwaysShow={!hasIdentifier}
                >
                    <View style={styles.oAuthButtonWrapper}>
                        <CleverLibraryButton
                            size="large"
                            isLogin={true}
                            reauth={true}
                            continueUrl={effectiveContinueUrl}
                        />
                    </View>
                </CleverLibraryActiveWrapper>
                {showClasslinkAuthProvider && (
                    <View style={styles.oAuthButtonWrapper}>
                        <ClasslinkButton continueURL={effectiveContinueUrl} />
                    </View>
                )}

                {hasAppleButton && hasIdentifier && (
                    <View style={styles.oAuthButtonWrapper}>
                        <AppleButton
                            onReauthSuccess={(url) => onSuccess()}
                            size="large"
                            continueUrl={effectiveContinueUrl}
                            context={authType.REAUTH}
                        />
                    </View>
                )}

                {/* Microsoft accounts do not require an identifier and, in
                    fact, don't provide a reliable one. */}
                {/* No wrapper here because it just adds a bottom margin and
                    that's not needed for the last button in this list */}
                {shouldShowMicrosoft && hasMicrosoftButton && (
                    <MicrosoftButton
                        size="large"
                        continueUrl={effectiveContinueUrl}
                    />
                )}
            </View>
        );
    };

    const renderErrorState = (errorState: string): React.ReactNode => {
        return (
            <View style={styles.errorContent}>
                <HeadingSmall style={styles.errorHeader}>
                    {t`Something went wrong.`}
                </HeadingSmall>
                <Body style={styles.errorBody}>
                    {strings.ssoFailureBody(errorState)}
                </Body>
                <Button
                    onClick={() => setSsoErrorState(null)}
                    kind="secondary"
                    style={styles.logInAgainButton}
                >
                    {t`Log in Another Way`}
                </Button>
            </View>
        );
    };

    const maybeRenderPasswordForm = (): React.ReactNode => {
        // Regardless of what options the props ask for, many of them require
        // the user to have an identifier (email or username) for the reauth to
        // work. We'll only show those options if `hasIdentifier` is true.
        const hasIdentifier = Boolean(identifier);

        if (!hasPasswordField || !hasIdentifier) {
            return null;
        }

        return (
            <form
                onSubmit={(e: React.SyntheticEvent) => {
                    e.preventDefault();
                    handleSubmit();
                }}
            >
                <PasswordTextField
                    testId="password-field"
                    label={t`Password`}
                    onValidate={(errorMessage?: string | null | undefined) => {
                        setIsValid(!errorMessage);
                        setServerError(null);
                    }}
                    onChange={(password) => {
                        setPassword(password);
                        setServerError(null);
                    }}
                />
                {serverError && (
                    <Body style={styles.errorBody}>{serverError}</Body>
                )}
                <Button
                    type="submit"
                    disabled={!password || isSubmitting || !isValid}
                    style={styles.logInButton}
                >
                    {renderLoginButtonText()}
                </Button>
            </form>
        );
    };

    const renderAuthenticatorForm = (): React.ReactNode => {
        return (
            <View>
                <HeadingSmall>
                    {doNotTranslateYet`Two-factor authentication`}
                </HeadingSmall>
                <Strut size={12} />
                <LabelMedium>
                    {doNotTranslateYet`Use your authenticator app to enter the verification code.`}
                </LabelMedium>
                <Strut size={16} />
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                >
                    <TextFieldWithInfo
                        testId="two-factor-authenticator-code"
                        id="two-factor-authenticator-code"
                        label={doNotTranslateYet`Verification code`}
                        required={true}
                        value={twoFactorAuthCode}
                        onChange={(code) => {
                            if (code.length <= 7) {
                                setTwoFactorAuthCode(code);
                            }
                        }}
                        placeholder={doNotTranslateYet`123 456`}
                    />
                    <View style={styles.recoveryOption}>
                        <LabelMedium>
                            {doNotTranslateYet`Can't access your app? `}
                        </LabelMedium>
                        <Button
                            kind="tertiary"
                            onClick={() => setLoginState("recovery")}
                        >
                            {doNotTranslateYet`Use a recovery code`}
                        </Button>
                    </View>
                    <Button
                        type="submit"
                        disabled={
                            twoFactorAuthCode.replace(" ", "").length !==
                                TWO_FACTOR_AUTH_CODE_LENGTH || isSubmitting
                        }
                        spinner={isSubmitting}
                    >
                        {doNotTranslateYet`Verify`}
                    </Button>
                </form>
            </View>
        );
    };

    const renderRecoveryForm = (): React.ReactNode => {
        return (
            <View>
                <HeadingSmall>{doNotTranslateYet`Two-factor authentication`}</HeadingSmall>
                <Strut size={12} />
                <LabelMedium>
                    {doNotTranslateYet`Enter a backup code from your list of recovery codes.`}
                </LabelMedium>
                <Strut size={16} />
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                >
                    <TextFieldWithInfo
                        testId="two-factor-recovery-code"
                        id="two-factor-recovery-code"
                        label={doNotTranslateYet`Recovery code`}
                        required={true}
                        value={twoFactorRecoveryCode}
                        onChange={(code) => {
                            if (code.length <= 11) {
                                setTwoFactorRecoveryCode(code);
                            }
                        }}
                        placeholder={doNotTranslateYet`abcde-12345`}
                    />
                    <Button
                        kind="tertiary"
                        onClick={() => setLoginState("authenticator")}
                        style={styles.leftAlignedButton}
                    >
                        {doNotTranslateYet`Use authenticator app instead`}
                    </Button>
                    <Strut size={12} />
                    <Button
                        type="submit"
                        disabled={
                            twoFactorRecoveryCode.trim().length !==
                                TWO_FACTOR_RECOVERY_CODE_LENGTH || isSubmitting
                        }
                    >
                        {isSubmitting
                            ? doNotTranslateYet`Verifying...`
                            : doNotTranslateYet`Verify`}
                    </Button>
                </form>
            </View>
        );
    };

    const renderModalContent = (): React.ReactNode => {
        if (ssoErrorState) {
            return renderErrorState(ssoErrorState);
        }

        // Authenticator app code step of two factor authentication
        if (loginState === "authenticator") {
            return renderAuthenticatorForm();
        }

        // Recovery code step of two factor authentication
        if (loginState === "recovery") {
            return renderRecoveryForm();
        }

        return (
            <View style={styles.content}>
                {renderOAuthButtons()}
                {maybeRenderPasswordForm()}
            </View>
        );
    };

    const modal = (
        <OnePaneDialog
            title={t`Let's confirm who you are.`}
            subtitle={t`For the security of your account, you need to login again to make this change.`}
            content={renderModalContent()}
        />
    );

    if (inlineForTests) {
        return modal;
    }

    return <ModalLauncher opened={true} onClose={onClose} modal={modal} />;
};

const styles = StyleSheet.create({
    content: {
        [mediaQueries.mdOrLarger]: {
            paddingInlineStart: sizing.size_240,
            paddingInlineEnd: sizing.size_240,
        },
        [mediaQueries.lgOrLarger]: {
            paddingInlineStart: sizing.size_640,
            paddingInlineEnd: sizing.size_640,
        },
    },
    errorContent: {
        [mediaQueries.mdOrLarger]: {
            paddingInlineStart: sizing.size_240,
            paddingInlineEnd: sizing.size_240,
        },
        [mediaQueries.lgOrLarger]: {
            paddingInlineStart: sizing.size_640,
            paddingInlineEnd: sizing.size_640,
        },
        alignItems: "center",
    },
    errorHeader: {
        paddingBlockStart: sizing.size_320,
        paddingBlockEnd: 8,
        textAlign: "center",
    },
    errorBody: {
        width: 368,
        paddingBlockEnd: sizing.size_240,
        textAlign: "center",
    },
    oAuthButtonWrapper: {
        marginBlockEnd: sizing.size_040,
    },
    oAuthButtons: {
        marginBlockEnd: sizing.size_320,
    },
    oAuthButtonsNoPassword: {
        paddingBlockStart: sizing.size_640,
    },
    logInButton: {
        marginBlockStart: 2,
    },
    logInAgainButton: {
        width: 185,
    },
    recoveryOption: {
        display: "flex",
        flexDirection: "row",
        alignItems: "baseline",
        gap: sizing.size_080,
    },
    leftAlignedButton: {
        alignSelf: "flex-start",
    },
});

export default memo(ReauthModal);
